// 面板中的素材库项点击打开视频弹窗（保留右下角添加素材按钮）
import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ChevronLeft } from "lucide-react";
import { Image } from "@/components/common/image";
import { TruncatedText } from "@/components/common/truncated-text";
import { MaterialModalDetail } from "@/components/material-modal-detail";
import { MaterialFolderItemData, userMaterialGetFolderItems } from "@/services/user-material-get-folder-items";
import { useTranslation } from "react-i18next";
import { MaterialEmptyImage } from "../agent-dashboard-user-detail/assets";
import { cn } from "@/lib/utils";
import { ModalCloseButton } from "../common/modal-close-button";
import { agentDashboardMaterialHooks } from "@/manager";
import folderBg from '@/assets/assets/folder-bg.webp';
import { MaterialsChooseModal } from "./materials-choose-modal";
import { MinusCircleIcon, PlusCircleIcon } from "../assets-material/assets";
import { useToast } from "@/hooks/use-toast";

type MaterialItem = {
  id: number;
  folderName: string;
  latestImages?: string[];
  createTime: number;
}

export const PanelAgentMaterialLibrary: React.FC<{
  open: boolean;
  materials?: MaterialItem[];
  saveLoading: boolean;
  onOpenChange: (open: boolean) => void;
  onAddMaterial?: (ids: number[], isAdd?: boolean) => Promise<boolean>;
}> = ({ open, onOpenChange, materials = [], saveLoading, onAddMaterial }) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<{ id: number; name: string } | null>(null);
  const [folderItems, setFolderItems] = useState<MaterialFolderItemData[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  // 素材库添加弹窗列表数据
  const { materialFolders, loading: materialFoldersLoading } = agentDashboardMaterialHooks.useMaterialFoldersData();

  const handleFolderClick = async (id: number, name: string) => {
    setSelectedFolder({ id, name });
    setDetailOpen(true);
    setItemsLoading(true);
    setFolderItems([]); // Clear previous items
    try {
      const res = await userMaterialGetFolderItems(id);
      setFolderItems(res.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setItemsLoading(false);
    }
  };

  // 保存添加
  const handleAdd = async (ids: number[]) => {
    try {
      // 合并id
      const success = await onAddMaterial?.([...ids, ...materials.map((m) => m.id)], true);
      if (!success) return;
      toast({
        description: t("agent_creation.subscription_settings.add_remove_modal.add_success"),
      })
    } catch (e) {
      console.error(e);
    }
  }

  // 保存移除
  const handleRemove = async (ids: number[]) => {
    try {
      // 过滤出不移除的id
      const removeIds = materials.filter((m) => !ids.includes(m.id)).map((m) => m.id);
      const success = await onAddMaterial?.(removeIds);
      if (!success) return;
      toast({
        description: t("agent_creation.subscription_settings.add_remove_modal.remove_success"),
      })
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/0" />
        <Dialog.Content
          className="
            fixed right-0 top-0 z-50 h-full w-[480px]
            bg-white flex flex-col
            data-[state=open]:translate-x-0 data-[state=closed]:translate-x-full
            transition-transform duration-300
          "
        >
          {/* 头部 */}
          <div className="flex items-center justify-between my-6 px-6">
            <div
              className="flex items-center gap-4 text-[rgba(16,24,40,1)]"
            >
              <ModalCloseButton
                icon={<ChevronLeft className="w-5 h-5" />}
                onClick={() => onOpenChange(false)}
              />
              <div className="text-base font-medium">
                {t("agent_creation.subscription_settings.material")}
              </div>
            </div>
            <ModalCloseButton
              onClick={() => onOpenChange(false)}
            />
          </div>

          {/* 全部素材库文件夹列表 */}
          <div className="flex-1 overflow-auto px-6">
            <div
              className={cn("grid gap-x-3 gap-y-4", !materials?.length && "min-h-full")}
              style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
            >
              {materials?.length ? materials.map((m) => (
                <button
                  key={m.id}
                  className="text-center inline-block w-full"
                  onClick={() => handleFolderClick(m.id, m.folderName)}
                >
                  <div className="mb-2 w-full overflow-hidden relative flex items-center justify-center flex-shrink-0">
                    <Image src={folderBg} className="size-full object-cover" alt={m.folderName} />
                    {m.latestImages?.[0] && (
                      <div className="absolute max-w-full max-h-full top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-full overflow-hidden flex items-center justify-center py-[5px] pt-5 px-1">
                        <Image
                          src={m.latestImages[0]}
                          className="max-h-full max-w-full object-contain rounded-[6px] border border-[rgba(0,0,0,0.1)]"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </div>
                  <TruncatedText className="max-w-[90px] text-sm text-[rgba(16,24,40,1)]" text={m.folderName} />
                </button>
              )) : (
                <div className=' w-full h-full relative flex flex-col justify-center items-center col-span-full gap-2'>
                  <MaterialEmptyImage className='w-[120px] h-[103px]' />
                  <span className='text-sm text-[rgba(102,112,133,1)]'>{t("agent_dashboard.user_detail.material.detail_modal.empty_tips2")}</span>
                </div>
              )}
            </div>
          </div>

          {/* 右下角添加素材按钮 */}
          <div className="absolute right-6 bottom-6 flex gap-3 items-center">
            <MaterialsChooseModal
              trigger={
                <button className="flex items-center gap-1.5 px-7 py-2.5 rounded-[8px] bg-white text-sm  text-nowrap whitespace-nowrap text-[rgba(16,24,40,1)] font-semibold hover:bg-gray-50 transition-colors border border-[rgba(208,213,221,1)]">
                  <MinusCircleIcon className="w-4 h-4 text-[rgba(16,24,40,1)]" />
                  <span>{t("agent_creation.subscription_settings.remove")}</span>
                </button>
              }
              title={t("agent_creation.subscription_settings.add_remove_modal.material_remove_title")}
              confirmText={t("agent_creation.subscription_settings.remove")}
              materialFolders={materials.map(item => ({
                id: item.id,
                folderName: item.folderName,
                createTime: item.createTime,
                showPic: item.latestImages?.[0] || '',
              }))}
              loading={saveLoading}
              onConfirm={handleRemove}
            />
            <MaterialsChooseModal
              trigger={
                <button className="flex items-center gap-1.5 px-7 py-2.5 rounded-[8px] bg-[rgba(16,24,40,1)] text-sm text-white text-nowrap whitespace-nowrap font-semibold hover:bg-[rgba(24,39,64,1)]/95 transition-colors">
                  <PlusCircleIcon className="w-4 h-4 text-white" />
                  <span>{t("agent_creation.subscription_settings.add")}</span>
                </button>
              }
              title={t("agent_creation.subscription_settings.add_remove_modal.material_add_title")}
              confirmText={t("agent_creation.subscription_settings.add")}
              materialFolders={materialFolders}
              loading={saveLoading}
              disabledIds={materials.map((m) => m.id)}
              onConfirm={handleAdd}
            />
          </div>
          <MaterialModalDetail
            open={detailOpen}
            onOpenChange={setDetailOpen}
            name={selectedFolder?.name}
            materialFolderItems={folderItems}
            loading={itemsLoading}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
